import { ChangeDetectionStrategy, Component, inject, signal, WritableSignal } from '@angular/core';
import { MatTreeModule } from '@angular/material/tree';
import { TableComponent } from "@shared/components/table/table.component";
import { TableDataGroupComponent } from "@shared/components/table/components/table-data-group/table-data-group.component";
import { ProjectModuleService } from './services/project-module.service';
import { ProjectModule, ProjectModules } from './types/project-module.type';
import { PROJECT_NUMBER } from '@features/project/constants/routes.constant';
import { TableSkeletonComponent } from "@shared/components/table/components/table-skeleton/table-skeleton.component";
import { IconModule } from '@ogs-gmbh/ngx-m3-components';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { MatButtonModule } from '@angular/material/button';
import { TableHeaderGroupComponent } from "@shared/components/table/components/table-header-group/table-header-group.component";
import { TableHeaderRowComponent } from "@shared/components/table/components/table-header-row/table-header-row.component";
import { MatCheckboxModule } from '@angular/material/checkbox';
import { provideTranslationScope, TranslationPipeModule } from '@ogs-gmbh/ngx-translate';
import { FormFooterComponent } from "../../../../../shared/components/form-footer/form-footer.component";
import { FormStateService } from '@core/services/form-state.service';
import { PROJECT_MODULE_TRANSLATION_SCOPE } from './consts/project-modules.translation';
import { NgTemplateOutlet } from '@angular/common';
import { OverviewComponent } from "@shared/components/templates/overview/overview.component";
import { OverviewHeaderComponent } from "@shared/components/overview-header/overview-header.component";
import { OverviewHeaderTitleComponent } from "@shared/components/overview-header/components/overview-header-title/overview-header-title.component";
import { STATUS_TYPES } from '@shared/types/status.types';
import { ComponentToastService } from '@shared/services/component-toast/component-toast.service';
import { SidebarService } from '@core/services/sidebar.service';

/**
 * Recursively sets all Submodules active based on the given boolean.
 * @param isActive whether submodules are set active or not
 *
 */
function setSubmodulesActiveStatus (isActive: boolean, submodules: readonly ProjectModule[]): void {
  for (const module of submodules) {
    if (module.submodules && module.submodules.length > 0 && module.isChecked)
      setSubmodulesActiveStatus(isActive, module.submodules);

    module.isActive = isActive;
  }
}

@Component({
  selector: 'app-project-modules',
  standalone: true,
  imports: [ MatTreeModule,
    TableComponent,
    TableDataGroupComponent,
    TableSkeletonComponent,
    IconModule,
    MatButtonModule,
    TableHeaderGroupComponent,
    TableHeaderRowComponent,
    MatCheckboxModule,
    TranslationPipeModule,
    FormFooterComponent,
    NgTemplateOutlet,
    OverviewComponent,
    OverviewHeaderComponent,
    OverviewHeaderTitleComponent ],
  providers: [
    provideTranslationScope(PROJECT_MODULE_TRANSLATION_SCOPE)
  ],
  templateUrl: './project-modules.component.html',
  styleUrl: './project-modules.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectModulesComponent {
  private readonly _faIconLibrary: FaIconLibrary = inject(FaIconLibrary);

  private readonly _projectToastService: ComponentToastService = inject(ComponentToastService);

  private readonly _sidebarService: SidebarService = inject(SidebarService);

  private readonly _projectNumber: string = inject(PROJECT_NUMBER);

  private readonly _formStateService: FormStateService = inject(FormStateService);

  private readonly _projectModuleService: ProjectModuleService = inject(ProjectModuleService);

  // eslint-disable-next-line @unicorn/no-useless-undefined
  protected readonly projectModules: WritableSignal<ProjectModules | undefined> = signal<ProjectModules | undefined>(undefined);


  constructor () {
    this._faIconLibrary.addIcons(faChevronRight, faChevronDown);
    this.getModules();
  }

  // eslint-disable-next-line @tseslint/class-methods-use-this
  protected readonly childrenAccessor = (node: ProjectModule): ProjectModule[] => node.submodules ?? [];

  // eslint-disable-next-line @tseslint/class-methods-use-this
  protected readonly hasChild = (_: number, node: ProjectModule): boolean =>
    node.submodules !== undefined && node.submodules.length > 0;


  private getModules (): void {
    this._projectModuleService.getModules(this._projectNumber)
      .subscribe((modules: ProjectModules) => {
        this.projectModules.set(modules);
        this._formStateService.clear();
      });
  }

  protected changeSelection (module: ProjectModule, isChecked: boolean): void {
    const projectModules: ProjectModules | undefined = this.projectModules();

    if (!projectModules) return;

    module.isChecked = isChecked;

    if (module.submodules) {
      setSubmodulesActiveStatus(isChecked, module.submodules);
      this.projectModules.set({ ...projectModules, modules: [ ...projectModules.modules ] });
    }

    this._formStateService.setValidity(true);
    this._formStateService.markDirty();
  }

  protected saveChanges (): void {
    const modules: ProjectModules | undefined = this.projectModules();

    if (!modules) return;

    this._projectModuleService.updateModules(this._projectNumber, modules)
      .subscribe({
        next: () => {
          this.getModules();
          this._sidebarService.reload();
          this._projectToastService
            .showToast(STATUS_TYPES.success, {
              defaultText: "Module wurden erfolgreich aktualisiert.",
              token: "status-modules-updated-successfully",
              scope: PROJECT_MODULE_TRANSLATION_SCOPE
            });
        },
        error: () => {
          this._projectToastService
            .showToast(STATUS_TYPES.error, {
              defaultText: "Unerwarteter Fehler beim aktualisieren der Module.",
              token: "status-module-update-error",
              scope: PROJECT_MODULE_TRANSLATION_SCOPE
            });
        }
      });
  }

  protected discardChanges (): void {
    this.getModules();
  }
}

